import { FileItem } from "./FileItem";

/**
 * File object with upload status information
 */
type FileWithStatus = {
  /** The file object */
  file: File;
  /** Current upload/processing status */
  status: "pending" | "uploading" | "success" | "error";
  /** Upload progress percentage (0-100) */
  progress: number;
  /** Error message if upload failed */
  error?: string;
  /** Result data from successful upload */
  result?: unknown;
};

type FileListProps = {
  /** Array of files with their status information */
  files: FileWithStatus[];
  /** Callback when preview button is clicked on a file */
  onPreview?: (file: File, index: number) => void;
  /** Callback when remove button is clicked on a file */
  onRemove: (file: File, index: number) => void;
  /** Function to format file size for display */
  formatFileSize: (bytes: number) => string;
};

/**
 * FileList component
 *
 * Renders a list of files with their status and action buttons.
 * Returns null if no files are present.
 *
 * @param files - Array of files with their status information
 * @param onPreview - Callback when preview button is clicked on a file
 * @param onRemove - Callback when remove button is clicked on a file
 * @param formatFileSize - Function to format file size for display
 */
export const FileList = ({
  files,
  onPreview,
  onRemove,
  formatFileSize,
}: FileListProps) => {
  if (files.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 space-y-2">
      {files.map((fileWithStatus, index) => (
        <FileItem
          key={`${fileWithStatus.file.name}-${index}`}
          file={fileWithStatus.file}
          index={index}
          status={fileWithStatus.status}
          error={fileWithStatus.error}
          onPreview={onPreview}
          onRemove={onRemove}
          formatFileSize={formatFileSize}
        />
      ))}
    </div>
  );
};
